import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Trophy, Plus, Edit, Trash, X, Car, Bike, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/currencyUtils';
import { useTicketAllocation } from '../../hooks/useTicketAllocation';

interface GlobalCategory {
  id: string;
  name: string;
  vehicle_type: 'car' | 'bike';
  time_range: string;
  tree_format: string;
  category_type: string;
  default_registration_fee: number;
  default_first_place_prize: number;
  default_second_place_prize: number;
  is_active: boolean;
  display_order: number;
  first_place_percentage?: number;
  second_place_percentage?: number;
  use_percentage_prizes?: boolean;
  re_entry_fee?: number;
  allows_re_entry?: boolean;
}

export default function CategoryManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<GlobalCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<GlobalCategory | null>(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState<'car' | 'bike'>('car');
  const [selectedTrackType, setSelectedTrackType] = useState<'drag' | 'drift'>('drag');
  const { settings: ticketSettings } = useTicketAllocation();

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    vehicle_type: 'car' as 'car' | 'bike',
    category_type: 'drag' as 'drag' | 'drift',
    time_range: '',
    tree_format: '',
    default_registration_fee: 0,
    default_first_place_prize: 0,
    default_second_place_prize: 0,
    is_active: true,
    first_place_percentage: 70,
    second_place_percentage: 30,
    use_percentage_prizes: false,
    re_entry_fee: 0,
    allows_re_entry: false
  });

  useEffect(() => {
    fetchCategories();
  }, [user]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('global_categories')
        .select('*')
        .order('display_order');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = {
        ...categoryForm,
        default_registration_fee: categoryForm.default_registration_fee || 0,
        default_first_place_prize: categoryForm.use_percentage_prizes ? 0 : categoryForm.default_first_place_prize || 0,
        default_second_place_prize: categoryForm.use_percentage_prizes ? 0 : categoryForm.default_second_place_prize || 0,
        first_place_percentage: categoryForm.use_percentage_prizes ? categoryForm.first_place_percentage : null,
        second_place_percentage: categoryForm.use_percentage_prizes ? categoryForm.second_place_percentage : null,
        use_percentage_prizes: categoryForm.use_percentage_prizes
      };
      
      if (selectedCategory) {
        const { error } = await supabase
          .from('global_categories')
          .update(formData)
          .eq('id', selectedCategory.id);

        if (error) throw error;
        toast.success('Category updated successfully');
      } else {
        const { error } = await supabase
          .from('global_categories')
          .insert([formData]);

        if (error) throw error;
        toast.success('Category created successfully');
      }

      setShowModal(false);
      setSelectedCategory(null);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category? This will affect all events using this category.')) return;

    try {
      const { error } = await supabase
        .from('global_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const resetForm = () => {
    setCategoryForm({
      name: '',
      vehicle_type: 'car',
      category_type: 'drag',
      time_range: '',
      tree_format: '',
      default_registration_fee: 0,
      default_first_place_prize: 0,
      default_second_place_prize: 0,
      is_active: true,
      first_place_percentage: 70,
      second_place_percentage: 30,
      use_percentage_prizes: false,
      re_entry_fee: 0,
      allows_re_entry: false
    });
  };

  // Filter categories based on selected track type and vehicle type
  const filteredCategories = categories.filter(category => {
    const matchesVehicleType = category.vehicle_type === selectedVehicleType;
    const matchesTrackType = category.category_type === selectedTrackType;
    return matchesVehicleType && matchesTrackType;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-white">Global Racing Categories</h2>
          <button
            onClick={() => {
              setSelectedCategory(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Category
          </button>
        </div>

        {/* Track Type Filter */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setSelectedTrackType('drag')}
            className={`px-4 py-2 rounded-lg ${
              selectedTrackType === 'drag'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Drag Racing
          </button>
          <button
            onClick={() => setSelectedTrackType('drift')}
            className={`px-4 py-2 rounded-lg ${
              selectedTrackType === 'drift'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Drift Racing
          </button>
        </div>

        {/* Vehicle Type Filter */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setSelectedVehicleType('car')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              selectedVehicleType === 'car'
                ? 'bg-blue-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            <Car className="h-4 w-4" />
            Cars
          </button>
          <button
            onClick={() => setSelectedVehicleType('bike')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              selectedVehicleType === 'bike'
                ? 'bg-green-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            <Bike className="h-4 w-4" />
            Bikes
          </button>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCategories.map((category) => (
            <div key={category.id} className="bg-zinc-900 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white font-semibold text-lg">{category.name}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      category.vehicle_type === 'car' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {category.vehicle_type === 'car' ? 'Car' : 'Bike'}
                    </span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      category.category_type === 'drag' ? 'bg-purple-100 text-purple-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {category.category_type === 'drag' ? 'Drag' : 'Drift'}
                    </span>
                    <span className="text-gray-400 text-sm">{category.time_range}</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {category.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {category.allows_re_entry && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                        Re-entry
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setCategoryForm({
                        name: category.name,
                        vehicle_type: category.vehicle_type,
                        category_type: category.category_type || 'drag',
                        time_range: category.time_range,
                        tree_format: category.tree_format,
                        default_registration_fee: category.default_registration_fee,
                        default_first_place_prize: category.default_first_place_prize,
                        default_second_place_prize: category.default_second_place_prize,
                        is_active: category.is_active,
                        first_place_percentage: category.first_place_percentage || 70,
                        second_place_percentage: category.second_place_percentage || 30,
                        use_percentage_prizes: category.use_percentage_prizes || false,
                        re_entry_fee: category.re_entry_fee || 0,
                        allows_re_entry: category.allows_re_entry || false
                      });
                      setShowModal(true);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="text-gray-400 hover:text-primary-500"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Registration Fee</p>
                  <p className="text-white font-semibold">{formatCurrency(category.default_registration_fee)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Prize Structure</p>
                  <p className="text-white font-semibold">
                    {category.use_percentage_prizes ? 
                      `${category.first_place_percentage || 70}% / ${category.second_place_percentage || 30}% split` : 
                      `Fixed prizes`}
                  </p>
                </div>
                {!category.use_percentage_prizes && (
                  <>
                    <div>
                      <p className="text-gray-400">1st Place Prize</p>
                      <p className="text-white font-semibold">{formatCurrency(category.default_first_place_prize)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">2nd Place Prize</p>
                      <p className="text-white font-semibold">{formatCurrency(category.default_second_place_prize)}</p>
                    </div>
                  </>
                )}
                {category.allows_re_entry && (
                  <div>
                    <p className="text-gray-400">Re-entry Fee</p>
                    <p className="text-white font-semibold">{formatCurrency(category.re_entry_fee || 0)}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 text-sm text-gray-400">
                <p><strong>Format:</strong> {category.tree_format}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Category Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">
                  {selectedCategory ? 'Edit Category' : 'Add Category'}
                </h2>
                <button onClick={() => setShowModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Vehicle Type *
                    </label>
                    <select
                      value={categoryForm.vehicle_type}
                      onChange={(e) => setCategoryForm({ ...categoryForm, vehicle_type: e.target.value as 'car' | 'bike' })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    >
                      <option value="car">Car</option>
                      <option value="bike">Bike</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Category Type *
                    </label>
                    <select
                      value={categoryForm.category_type}
                      onChange={(e) => setCategoryForm({ ...categoryForm, category_type: e.target.value as 'drag' | 'drift' })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    >
                      <option value="drag">Drag Racing</option>
                      <option value="drift">Drift Racing</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Time Range
                    </label>
                    <input
                      type="text"
                      value={categoryForm.time_range}
                      onChange={(e) => setCategoryForm({ ...categoryForm, time_range: e.target.value })}
                      placeholder="e.g., 9.20 – 10.50"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Tree Format
                    </label>
                    <input
                      type="text"
                      value={categoryForm.tree_format}
                      onChange={(e) => setCategoryForm({ ...categoryForm, tree_format: e.target.value })}
                      placeholder="e.g., Full tree, 500, Bracket"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Registration Fee (XCG) *
                    </label>
                    <input
                      type="number"
                      value={categoryForm.default_registration_fee}
                      onChange={(e) => setCategoryForm({ ...categoryForm, default_registration_fee: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    />
                  </div>
                  
                  {/* Prize Structure Selection */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Prize Structure
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setCategoryForm({ ...categoryForm, use_percentage_prizes: false })}
                        className={`p-4 rounded-lg text-left transition-colors ${
                          !categoryForm.use_percentage_prizes
                            ? 'bg-primary-600 text-white'
                            : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                        }`}
                      >
                        <div className="font-semibold mb-1">Fixed Prize Amounts</div>
                        <div className="text-sm opacity-80">
                          Set specific prize amounts for 1st and 2nd place
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCategoryForm({ ...categoryForm, use_percentage_prizes: true })}
                        className={`p-4 rounded-lg text-left transition-colors ${
                          categoryForm.use_percentage_prizes
                            ? 'bg-primary-600 text-white'
                            : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                        }`}
                      >
                        <div className="font-semibold mb-1">Percentage of Money Pot</div>
                        <div className="text-sm opacity-80">
                          Winners get a percentage of total registration fees
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Conditional fields based on prize structure */}
                  {categoryForm.use_percentage_prizes ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          1st Place Percentage (%)
                        </label>
                        <input
                          type="number"
                          value={categoryForm.first_place_percentage}
                          onChange={(e) => setCategoryForm({ 
                            ...categoryForm, 
                            first_place_percentage: parseFloat(e.target.value) || 0
                          })}
                          min="0"
                          max="100"
                          step="1"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          2nd Place Percentage (%)
                        </label>
                        <input
                          type="number"
                          value={categoryForm.second_place_percentage}
                          onChange={(e) => setCategoryForm({ 
                            ...categoryForm, 
                            second_place_percentage: parseFloat(e.target.value) || 0
                          })}
                          min="0"
                          max="100"
                          step="1"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                        />
                      </div>
                      <div className="md:col-span-2 bg-amber-900/30 border border-amber-800/30 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Info className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-300 text-sm font-medium">Percentage Prize Structure</p>
                            <p className="text-amber-200 text-sm mt-1">
                              With this option, prize money will be calculated as a percentage of the total registration fees collected for this category.
                              First place will receive {categoryForm.first_place_percentage}% and second place will receive {categoryForm.second_place_percentage}%.
                            </p>
                            <p className="text-amber-200 text-sm mt-1">
                              Note: Percentages don't need to add up to 100%. You can set any percentage values.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          1st Place Prize (XCG)
                        </label>
                        <input
                          type="number"
                          value={categoryForm.default_first_place_prize}
                          onChange={(e) => setCategoryForm({ ...categoryForm, default_first_place_prize: parseFloat(e.target.value) || 0 })}
                          min="0"
                          step="0.01"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          2nd Place Prize (XCG)
                        </label>
                        <input
                          type="number"
                          value={categoryForm.default_second_place_prize}
                          onChange={(e) => setCategoryForm({ ...categoryForm, default_second_place_prize: parseFloat(e.target.value) || 0 })}
                          min="0"
                          step="0.01"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                        />
                      </div>
                    </>
                  )}
                </div>
                
                <div className="bg-blue-900/30 border border-blue-800/30 rounded-lg p-4 my-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-blue-300 text-sm">
                      <p className="font-medium mb-1">Ticket & Wristband Allocation</p>
                      <p>Tickets and wristbands are automatically allocated based on system settings:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>First category: {ticketSettings.ticket_base_amount} tickets</li>
                        <li>Additional categories: {ticketSettings.ticket_additional_amount} tickets each</li>
                        <li>Maximum tickets per driver: {ticketSettings.ticket_max_amount}</li>
                        <li>Wristbands per category: {ticketSettings.wristband_per_category}</li>
                        <li>Maximum wristbands per driver: {ticketSettings.wristband_max_amount}</li>
                      </ul>
                      <p className="mt-2">You can adjust these settings in the Ticket Settings section.</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={categoryForm.is_active}
                      onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-300">
                      Active
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allows_re_entry"
                      checked={categoryForm.allows_re_entry}
                      onChange={(e) => setCategoryForm({ ...categoryForm, allows_re_entry: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="allows_re_entry" className="text-sm font-medium text-gray-300">
                      Allow Re-entry
                    </label>
                  </div>
                </div>
                
                {categoryForm.allows_re_entry && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Re-entry Fee (XCG)
                    </label>
                    <input
                      type="number"
                      value={categoryForm.re_entry_fee}
                      onChange={(e) => setCategoryForm({ ...categoryForm, re_entry_fee: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Fee charged when drivers request re-entry for this category
                    </p>
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                >
                  {selectedCategory ? 'Update Category' : 'Create Category'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}